import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { BookText, Eye, EyeOff, Plus, Loader2, Pencil, Check, X, Search, ChevronDown, Trash2 } from "lucide-react";
import { toast } from "sonner";

const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

function FraseRow({ fr, i, onPick, onSaved }) {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/studio/frasario-diario/elimina", { index: i, old_testo: fr.testo });
      onSaved(data.items || []); setConfirmDel(false); toast.success("Frase eliminata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const save = async () => {
    if (!edit.trim()) { toast.error("Inserisci il testo della frase"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/studio/frasario-diario/modifica", { index: i, old_testo: fr.testo, testo: edit.trim() });
      onSaved(data.items || []); setEdit(null); toast.success("Frase aggiornata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const vis = fr.visibile_cliente ? <Eye className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> : <EyeOff className="h-3.5 w-3.5 text-red-500 shrink-0" />;
  if (confirmDel) return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-500/10" data-testid={`diario-frasario-del-confirm-${i}`}>
      <span className="flex-1 min-w-0 truncate text-sm text-red-500" title={fr.testo}>Eliminare «{fr.testo}»?</span>
      <button type="button" onClick={remove} disabled={busy} data-testid={`diario-frasario-del-yes-${i}`} className="h-7 px-2.5 rounded text-xs font-semibold bg-red-600 text-white hover:bg-red-700">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Elimina"}</button>
      <button type="button" onClick={() => setConfirmDel(false)} disabled={busy} data-testid={`diario-frasario-del-no-${i}`} className="h-7 px-2.5 rounded text-xs text-slate-400 hover:bg-slate-700/40">Annulla</button>
    </div>
  );
  if (edit !== null) return (
    <div className="flex items-center gap-1.5 px-2 py-1" data-testid={`diario-frasario-edit-${i}`}>
      {vis}
      <input autoFocus value={edit} onChange={(e) => setEdit(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { e.stopPropagation(); setEdit(null); } }}
        data-testid={`diario-frasario-edit-input-${i}`} className="flex-1 min-w-0 h-8 rounded-md border border-slate-600 bg-slate-900 px-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-500" />
      <button type="button" onClick={save} disabled={busy} title="Salva" data-testid={`diario-frasario-edit-save-${i}`} className="p-1.5 rounded text-emerald-500 hover:bg-emerald-500/10">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}</button>
      <button type="button" onClick={() => setEdit(null)} title="Annulla" data-testid={`diario-frasario-edit-cancel-${i}`} className="p-1.5 rounded text-slate-400 hover:bg-slate-700/40"><X className="h-4 w-4" /></button>
    </div>
  );
  return (
    <div className="group flex items-center gap-1 rounded-md hover:bg-slate-800/60">
      <button type="button" onClick={() => onPick(fr)} data-testid={`diario-frasario-opt-${i}`} className="flex-1 min-w-0 flex items-center gap-2 px-2 py-1.5 text-left text-sm">
        {vis}<span className="truncate" title={fr.testo}>{fr.testo}</span>
      </button>
      <button type="button" onClick={() => setEdit(fr.testo)} title="Modifica frase" data-testid={`diario-frasario-edit-btn-${i}`} className="p-1.5 rounded text-slate-400 hover:text-amber-400 hover:bg-slate-700/40"><Pencil className="h-3.5 w-3.5" /></button>
      <button type="button" onClick={() => setConfirmDel(true)} title="Elimina frase" data-testid={`diario-frasario-del-btn-${i}`} className="p-1.5 mr-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

// Menu "Frasario" del Diario: ricerca predittiva, scelta e modifica delle frasi predefinite.
export default function FrasarioSelect({ onPick }) {
  const [items, setItems] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/studio/frasario-diario").then(({ data }) => setItems(data || [])).catch(() => {}); }, []);
  const shown = useMemo(() => items.map((fr, i) => ({ fr, i })).filter(({ fr }) => !q.trim() || norm(fr.testo).includes(norm(q.trim()))), [items, q]);
  const pick = (fr) => { onPick(fr); setOpen(false); setQ(""); };
  return (
    <>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
        <PopoverTrigger asChild>
          <button type="button" data-testid="diario-frasario" className="h-9 w-72 flex items-center justify-between gap-2 rounded-md border border-slate-700 bg-slate-900/60 px-3 text-sm">
            <span className="flex items-center gap-1.5 text-slate-300"><BookText className="h-4 w-4 text-amber-400" />Frasario…</span>
            <ChevronDown className="h-4 w-4 text-slate-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[28rem] p-1.5 bg-[#121824] border-slate-700 text-slate-100" data-testid="diario-frasario-menu">
          <div className="relative mb-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nel frasario…" data-testid="diario-frasario-search"
              className="w-full h-9 rounded-md border border-slate-700 bg-slate-900/60 pl-8 pr-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-500" />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {shown.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-500" data-testid="diario-frasario-empty">{items.length ? "Nessuna frase contiene il testo cercato." : "Nessuna frase nel frasario."}</div>
            ) : shown.map(({ fr, i }) => <FraseRow key={`${i}-${fr.testo}`} fr={fr} i={i} onPick={pick} onSaved={setItems} />)}
          </div>
          <div className="border-t border-slate-700 mt-1 pt-1">
            <button type="button" onClick={() => { setOpen(false); setNewOpen(true); }} data-testid="diario-frasario-add" className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-semibold text-amber-400 hover:bg-slate-800/60"><Plus className="h-3.5 w-3.5 shrink-0" /> Aggiungi nuova frase…</button>
          </div>
        </PopoverContent>
      </Popover>
      <NuovaFraseDialog open={newOpen} onOpenChange={setNewOpen} onSaved={(d) => { setItems(d.items || []); if (d.item) onPick(d.item); }} />
    </>
  );
}
function NuovaFraseDialog({ open, onOpenChange, onSaved }) {
  const [testo, setTesto] = useState("");
  const [vis, setVis] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTesto(""); setVis(false); } }, [open]);
  const save = async () => {
    if (!testo.trim()) { toast.error("Inserisci il testo della frase"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/studio/frasario-diario/aggiungi", { testo: testo.trim(), visibile_cliente: vis });
      toast.success("Frase aggiunta al frasario");
      onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-md" data-testid="frasario-new-dialog">
        <DialogHeader><DialogTitle className="font-head">Aggiungi nuova frase</DialogTitle></DialogHeader>
        <Textarea autoFocus value={testo} onChange={(e) => setTesto(e.target.value)} rows={3} placeholder="Testo della frase…" data-testid="frasario-new-text" className="bg-slate-900/60 border-slate-700" />
        <label className="flex items-center justify-between gap-3 text-sm text-slate-300">
          <span className="flex items-center gap-2">{vis ? <Eye className="h-4 w-4 text-emerald-400" /> : <EyeOff className="h-4 w-4 text-red-500" />} Visibile al cliente</span>
          <Switch checked={vis} onCheckedChange={setVis} data-testid="frasario-new-visibile" className="data-[state=checked]:bg-emerald-500" />
        </label>
        <DialogFooter>
          <Button onClick={save} disabled={busy || !testo.trim()} data-testid="frasario-new-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva e usa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

